import type { GroupOption } from '@exam-management/common/types';
import {
  ALL_GROUP_OPTION,
  filterAndInsertOption,
  UNASSIGNED_OPTION,
} from '@exam-management/common/utils';
import {
  useGetExamCourseGroupByIdQuery,
  useSearchExamCourseGroupsLazyQuery,
} from '@exam-management/graphql';
import { SearchSelection } from '@shared/components';
import { useMemo, useState } from 'react';

import { useExamCourseSearchParams } from '../../hooks';

export default function ExaminationCourseGroupSelection() {
  const [{ group }, setExamCourseSearchParams] = useExamCourseSearchParams();
  const [inputValue, setInputValue] = useState<string | null>(null);

  const isUnassigned = group === UNASSIGNED_OPTION.value;

  const { data: examCourseGroupData, loading: isGettingExamCourse } =
    useGetExamCourseGroupByIdQuery({
      skip: group === '' || isUnassigned,
      variables: {
        getExamCourseGroupByIdInput: {
          examCourseGroupId: group,
        },
      },
    });

  const [
    searchExamCourseGroups,
    { data: examCourseGroupsData, loading: isSearchingExamCourseGroups },
  ] = useSearchExamCourseGroupsLazyQuery();

  const examCoursesOptions = useMemo(() => {
    if (examCourseGroupsData) {
      const examCourseGroupsOptions =
        examCourseGroupsData.excSearchExamCourseGroupsByName.tags.map<GroupOption>((tag) => {
          return {
            label: tag.name,
            value: tag.id,
          };
        });
      return filterAndInsertOption(
        [ALL_GROUP_OPTION, UNASSIGNED_OPTION],
        examCourseGroupsOptions,
        inputValue ?? '',
        ALL_GROUP_OPTION,
      );
    } else {
      return [];
    }
  }, [examCourseGroupsData, inputValue]);

  const currentExamCourse = examCourseGroupData
    ? {
        label: examCourseGroupData.excGetExamCourseGroupById.tag.name,
        value: examCourseGroupData.excGetExamCourseGroupById.tag.id,
      }
    : ALL_GROUP_OPTION;

  const isLoading = isSearchingExamCourseGroups || isGettingExamCourse;
  const value = isUnassigned ? UNASSIGNED_OPTION : currentExamCourse;

  return (
    <SearchSelection
      label='Group'
      mode='fetch-debounce'
      value={value ?? null}
      inputValue={!isGettingExamCourse ? (inputValue ?? value?.label) : ''}
      defaultValue={ALL_GROUP_OPTION}
      options={examCoursesOptions}
      loading={isLoading}
      disabled={isGettingExamCourse}
      onSearch={(inputValue) => {
        const isDefaultValue = inputValue === ALL_GROUP_OPTION.label;
        searchExamCourseGroups({
          variables: {
            searchExamCourseGroupsByNameInput: {
              name: !isDefaultValue && inputValue?.trim() ? inputValue : undefined,
            },
          },
          fetchPolicy: 'cache-and-network',
        });
      }}
      onChange={(_, option) => {
        setExamCourseSearchParams({
          group: option?.value?.toString() ?? '',
          page: null,
        });
        setInputValue(option?.label ?? null);
      }}
      onInputChange={(_, value) => {
        setInputValue(value);
      }}
    />
  );
}
